/**
 * The league runs on Eastern time, so dates are formatted in that zone
 * regardless of where the reader happens to be.
 */
const LEAGUE_TZ = 'America/New_York';

export function formatDateTime(iso: string): string {
  return new Intl.DateTimeFormat('en-US', {
    timeZone: LEAGUE_TZ,
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(iso));
}

/**
 * To the second, with the zone — for "exactly when did this last finish".
 * `timeStyle: 'long'` is what carries the zone name; asking for `timeZoneName`
 * alongside a style is a TypeError.
 */
export function formatExactTime(iso: string): string {
  return new Intl.DateTimeFormat('en-US', {
    timeZone: LEAGUE_TZ,
    dateStyle: 'medium',
    timeStyle: 'long',
  }).format(new Date(iso));
}

export function formatDate(iso: string): string {
  return new Intl.DateTimeFormat('en-US', {
    timeZone: LEAGUE_TZ,
    dateStyle: 'medium',
  }).format(new Date(iso));
}

/**
 * A bare `YYYY-MM-DD` important date, broken into the pieces a calendar tile
 * shows, with the time beside it. Parsed as a local calendar date rather than
 * converted from a timezone, so "Aug 30" never renders as "Aug 29".
 */
export function calendarParts(
  date: string,
  time: string | null,
): { month: string; day: string; year: number; weekday: string; time: string | null } | null {
  const [year, month, day] = date.split('-').map(Number);
  if (!year || !month || !day) return null;

  const on = new Date(year, month - 1, day);
  const part = (options: Intl.DateTimeFormatOptions) =>
    new Intl.DateTimeFormat('en-US', options).format(on);

  const [hours, minutes] = (time ?? '').split(':').map(Number);

  return {
    month: part({ month: 'short' }),
    day: part({ day: 'numeric' }),
    year,
    weekday: part({ weekday: 'long' }),
    time: time
      ? new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit' }).format(
          new Date(year, month - 1, day, hours ?? 0, minutes ?? 0),
        )
      : null,
  };
}

/** Just the clock, for a chat line that already sits under its own day. */
export function formatTimeOfDay(iso: string): string {
  return new Intl.DateTimeFormat('en-US', {
    timeZone: LEAGUE_TZ,
    hour: 'numeric',
    minute: '2-digit',
  }).format(new Date(iso));
}

/**
 * The day a timestamp fell on in league time, as a key a run of messages can be
 * grouped under. Built from the formatted parts rather than from the ISO string,
 * which is UTC and would break a late-evening conversation across two days.
 */
export function leagueDayKey(iso: string): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: LEAGUE_TZ, dateStyle: 'short' }).format(
    new Date(iso),
  );
}

/** That day, written out: "Sunday, September 8, 2025". */
export function formatLongDay(iso: string): string {
  return new Intl.DateTimeFormat('en-US', {
    timeZone: LEAGUE_TZ,
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }).format(new Date(iso));
}

/**
 * How far off an instant is, in the largest unit that still says something.
 *
 * Unlike `relativeDays` this takes a timestamp rather than a calendar date, for
 * the things that expire at a particular moment: "in 12 days" is the useful
 * reading of a session that runs out a fortnight from now, and "3 hours ago" of
 * one that has already gone.
 */
export function relativeTime(iso: string, now: number = Date.now()): string {
  const at = new Date(iso).getTime();
  if (!Number.isFinite(at)) return '';

  const difference = at - now;
  const format = new Intl.RelativeTimeFormat('en-US', { numeric: 'auto' });
  const units: Array<[Intl.RelativeTimeFormatUnit, number]> = [
    ['day', 86_400_000],
    ['hour', 3_600_000],
    ['minute', 60_000],
  ];

  for (const [unit, size] of units) {
    if (Math.abs(difference) >= size) return format.format(Math.round(difference / size), unit);
  }
  return format.format(Math.round(difference / 60_000), 'minute');
}

export function relativeDays(date: string): string {
  const [year, month, day] = date.split('-').map(Number);
  if (!year || !month || !day) return '';

  const target = new Date(year, month - 1, day);
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const days = Math.round((target.getTime() - today.getTime()) / 86_400_000);
  if (days === 0) return 'today';
  if (days === 1) return 'tomorrow';
  if (days < 0) return `${Math.abs(days)} days ago`;
  return `in ${days} days`;
}
